"use client";

import { useMemo } from "react";

import {
  RentComparisonPanel,
  type RentComparisonOption,
  type RentComparisonSuggestion,
} from "@/components/portfolio/RentComparisonPanel";
import { useAppContext } from "@/context/AppContext";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import type { SuggestedTransactionPreference, Transaction } from "@/types";

function normalizeTransactionName(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase();
}

function getCurrentRent(transactions: Transaction[]) {
  const today = new Date();

  return (
    transactions
      .filter((transaction) => {
        const name = normalizeTransactionName(transaction.name);
        const startsAt = new Date(transaction.dateFrom);
        const endsAt = transaction.dateTo ? new Date(transaction.dateTo) : null;

        return (
          transaction.transactionType === "INCOME" &&
          transaction.recurrence === "MONTHLY" &&
          (name.includes("najem") || name.includes("rent")) &&
          startsAt <= today &&
          (!endsAt || endsAt >= today)
        );
      })
      .sort(
        (first, second) =>
          new Date(second.dateFrom).getTime() -
          new Date(first.dateFrom).getTime(),
      )[0]?.amount ?? 0
  );
}

export function PortfolioRenovationPotential() {
  const { language } = useLanguage();
  const { userPreferences, updateUserPreferences } = useAppContext();
  const {
    analysis,
    isLoadingAnalysis,
    selectedEstate,
  } = useEstateContext();
  const copy = {
    cs: {
      title: "Nájem a hodnota",
      description: "Srovnání nájmu a odhadované hodnoty.",
      current: "Aktuální",
      suggested: "Doporučený",
      renovated: "Po rekonstrukci",
      value: "Odhad hodnoty",
      valueIncrease: "Nárůst",
      rent: "Nájem / měsíc",
      noChange: "Beze změny",
      notWorthIt: "Rekonstrukce se nevyplatí",
      rentWillNotIncrease: "Rekonstrukce nájem nezvýší",
      raiseRent: "Doporučujeme zvýšit nájem",
      renovateForProfitability:
        "Doporučujeme provést rekonstrukci pro zvýšení rentability",
      hideSuggestion: "Skrýt toto doporučení",
      waitingForComparables: "Čekáme na srovnatelné nabídky",
      waitingForComparablesDetail:
        "Doporučený nájem a odhad po rekonstrukci doplníme, až se v okolí objeví podobné nemovitosti. Podle lokality to může chvíli trvat.",
      locale: "cs-CZ",
      currency: "CZK",
    },
    en: {
      title: "Rent and value",
      description: "Rent and estimated value comparison.",
      current: "Current",
      suggested: "Suggested",
      renovated: "After renovation",
      value: "Estimated value",
      valueIncrease: "Increase",
      rent: "Monthly rent",
      noChange: "No change",
      notWorthIt: "Renovation is not worth it yet",
      rentWillNotIncrease: "Renovation will not increase the rent",
      raiseRent: "We recommend increasing the rent",
      renovateForProfitability:
        "We recommend renovating to increase profitability",
      hideSuggestion: "Hide this suggestion",
      waitingForComparables: "Waiting for comparable listings",
      waitingForComparablesDetail:
        "We will add suggested rent and the post-renovation estimate when similar properties appear nearby. Depending on the location, this may take some time.",
      locale: "en-US",
      currency: "CZK",
    },
  }[language];

  const currentValue = analysis.avgPriceSell;
  const currentRent = useMemo(
    () => getCurrentRent(selectedEstate?.transactions ?? []),
    [selectedEstate?.transactions],
  );
  const suggestedRent = analysis.avgPriceBefore;
  const renovatedRent = analysis.avgPriceAfter;
  const hasSuggestedEstimate = suggestedRent > 0;
  const hasRenovatedEstimate = renovatedRent > 0;
  const isWaitingForMarketData =
    !isLoadingAnalysis && (!hasSuggestedEstimate || !hasRenovatedEstimate);
  const renovatedValue =
    hasRenovatedEstimate &&
    currentValue > 0 &&
    currentRent > 0 &&
    renovatedRent > currentRent
      ? Math.round(currentValue * (renovatedRent / currentRent))
      : currentValue;
  const valueIncrease = Math.max(0, renovatedValue - currentValue);
  const formatCurrency = (value: number) =>
    value > 0
      ? new Intl.NumberFormat(copy.locale, {
          style: "currency",
          currency: copy.currency,
          maximumFractionDigits: 0,
        }).format(value)
      : "—";
  const formatIncrease = (value: number) =>
    new Intl.NumberFormat(copy.locale, {
      style: "currency",
      currency: copy.currency,
      maximumFractionDigits: 0,
    }).format(value);
  const displayMetric = (value: number) =>
    isLoadingAnalysis ? "—" : formatCurrency(value);
  const getIncrease = (rent: number) =>
    currentRent > 0 && rent > currentRent ? rent - currentRent : 0;
  const getIncreaseLabel = (rent: number) => {
    const increase = getIncrease(rent);
    return increase > 0 ? `+${formatCurrency(increase)}` : formatIncrease(0);
  };
  const options: RentComparisonOption[] = [
    {
      id: "current",
      label: copy.current,
      rent: displayMetric(currentRent),
      increase: copy.noChange,
    },
    {
      id: "suggested",
      label: copy.suggested,
      rent: displayMetric(suggestedRent),
      increase: getIncreaseLabel(suggestedRent),
      increaseTone: getIncrease(suggestedRent) > 0 ? "positive" : "default",
      percentage: {
        valueNow: suggestedRent,
        valueBefore: currentRent,
      },
    },
    {
      id: "renovated",
      label: copy.renovated,
      rent: displayMetric(renovatedRent),
      increase: getIncreaseLabel(renovatedRent),
      increaseTone: getIncrease(renovatedRent) > 0 ? "positive" : "default",
      percentage: {
        valueNow: renovatedRent,
        valueBefore: currentRent,
      },
    },
  ];
  const estatePreferenceKey = selectedEstate
    ? String(selectedEstate.id)
    : null;
  const hiddenSuggestions = estatePreferenceKey
    ? (userPreferences.hideSuggestedBox[estatePreferenceKey] ?? [])
    : [];
  const hideSuggestion = (key: SuggestedTransactionPreference) => {
    if (!estatePreferenceKey) return;

    void updateUserPreferences({
      hideSuggestedBox: {
        ...userPreferences.hideSuggestedBox,
        [estatePreferenceKey]: hiddenSuggestions.includes(key)
          ? hiddenSuggestions
          : [...hiddenSuggestions, key],
      },
    });
  };
  const createSuggestion = (
    key: SuggestedTransactionPreference,
    label: string,
  ): RentComparisonSuggestion | null =>
    hiddenSuggestions.includes(key)
      ? null
      : {
          key,
          label,
          dismissLabel: copy.hideSuggestion,
          onDismiss: () => hideSuggestion(key),
        };
  const valueSuggestions: RentComparisonSuggestion[] = [];
  const rentSuggestions: RentComparisonSuggestion[] = [];

  if (currentValue > 0 && renovatedValue > 0 && valueIncrease < 500_000) {
    const suggestion = createSuggestion(
      "renovationValue",
      copy.notWorthIt,
    );
    if (suggestion) valueSuggestions.push(suggestion);
  }
  if (
    hasSuggestedEstimate &&
    hasRenovatedEstimate &&
    suggestedRent === renovatedRent
  ) {
    const suggestion = createSuggestion(
      "renovationRent",
      copy.rentWillNotIncrease,
    );
    if (suggestion) rentSuggestions.push(suggestion);
  }
  if (
    currentRent > 0 &&
    hasSuggestedEstimate &&
    suggestedRent - currentRent > 1_500
  ) {
    const suggestion = createSuggestion("rentIncrease", copy.raiseRent);
    if (suggestion) rentSuggestions.push(suggestion);
  }
  if (
    currentRent > 0 &&
    hasRenovatedEstimate &&
    renovatedRent - currentRent > 3_000
  ) {
    const suggestion = createSuggestion(
      "renovationProfitability",
      copy.renovateForProfitability,
    );
    if (suggestion) rentSuggestions.push(suggestion);
  }

  return (
    <RentComparisonPanel
      title={copy.title}
      description={copy.description}
      valueLabel={copy.value}
      currentValueLabel={copy.current}
      currentValue={displayMetric(currentValue)}
      renovatedValueLabel={copy.renovated}
      renovatedValue={displayMetric(renovatedValue)}
      valueIncreaseLabel={copy.valueIncrease}
      valueIncrease={
        valueIncrease > 0
          ? `+${formatCurrency(valueIncrease)}`
          : formatIncrease(0)
      }
      valuePercentage={{
        valueNow: renovatedValue,
        valueBefore: currentValue,
      }}
      valueSuggestions={valueSuggestions}
      rentSuggestions={rentSuggestions}
      waitingForMarketData={
        isWaitingForMarketData
          ? {
              label: copy.waitingForComparables,
              detail: copy.waitingForComparablesDetail,
            }
          : undefined
      }
      rentLabel={copy.rent}
      options={options}
      locale={copy.locale}
    />
  );
}
